import React from "react";
import Layout from "../PageLayout/Layout";
import FAQSection from "../Pricing/FAQSection";
import "./ProductInsurance.css";

export default function FeatureComparison() {
  const rows = [
    {
      feature: "Compliance & Security",
      corelink: "✅ SOC 2–aligned, HIPAA/GDPR ready",
      general: "⚠️ Partial",
      legacy: "✅ Mature, but slow and rigid",
    },
    {
      feature: "Context Awareness",
      corelink: "✅ Learns company policies, SOPs, workflows",
      general: "❌ Generic / one-size-fits-all",
      legacy: "⚠️ Limited to predefined workflows",
    },
    {
      feature: "Deep Integrations",
      corelink: "✅ 50+ connectors (Salesforce, SAP, Teams, etc.)",
      general: "❌ Few native integrations",
      legacy: "⚠️ Requires heavy customization",
    },
    {
      feature: "Real-Time Answers",
      corelink: "✅ Sub-2s responses",
      general: "✅ Variable",
      legacy: "⚠️ Manual search and ticketing",
    },
    {
      feature: "Workflow Automation",
      corelink: "✅ End-to-end orchestration",
      general: "⚠️ Basic / single-step",
      legacy: "✅ Scripted, not adaptive",
    },
    {
      feature: "Audit Trail & Traceability",
      corelink: "✅ Full system-level logging",
      general: "❌ None",
      legacy: "✅ Manual logs only",
    },
    {
      feature: "Private AI (No Training on Your Data)",
      corelink: "✅ 100% Private",
      general: "❌ Shared model context",
      legacy: "✅ Private, but not AI-native",
    },
    {
      feature: "Deployment Speed",
      corelink: "⚡ Weeks, not months",
      general: "⚡ Instant but unsecured",
      legacy: "🐢 Long enterprise rollout",
    },
    {
      feature: "Ecosystem Neutrality",
      corelink: "✅ Works across Microsoft, Google, Salesforce",
      general: "❌ Vendor-locked",
      legacy: "❌ Vendor-locked",
    },
    {
      feature: "ROI Timeline",
      corelink: "⏱ <90 days",
      general: "❌ Unproven",
      legacy: "🐢 6–12 months typical",
    },
  ];

  return (
    <>
      {/* Mobile — unchanged comparison table */}
      <section className="py-16 px-4 pi-mobile">
        <Layout>
          <div className="mb-12 text-center px-4 sm:px-6">
            <div className="internal-hero-badge mb-6 inline-flex items-center gap-2">
              <img src="/icons/arrows-left-right.svg" alt="arrows-left-right Icon" />
              <span>See the Difference</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold mb-4 text-gray-900">
              The Trusted Intelligence Layer Your Enterprise Stack Has Been Missing
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
              Highwatch goes beyond general AI and legacy enterprise systems, it unifies every system, understands your business context, and acts securely across workflows.
              Where others assist, Corelink executes, compliantly, contextually, and in seconds.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto max-w-[1200px] mx-auto">
            <table className="w-full min-w-[900px] table-fixed text-left text-sm align-middle">
              <colgroup>
                <col style={{ width: "30%" }} />
                <col style={{ width: "23.33%" }} />
                <col style={{ width: "23.33%" }} />
                <col style={{ width: "23.33%" }} />
              </colgroup>
              <thead>
                <tr className="text-gray-800 border-b border-gray-200 bg-gray-50">
                  <th className="px-6 py-5 font-semibold align-middle">Features</th>
                  <th className="px-6 py-5 font-semibold align-middle">Highwatch AI</th>
                  <th className="px-6 py-5 font-semibold align-middle">General AI Platforms</th>
                  <th className="px-6 py-5 font-semibold align-middle">Legacy Enterprise Platforms</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-6 py-5 align-middle">
                      <div className="font-medium text-gray-800">{r.feature}</div>
                    </td>
                    <td className="px-6 py-5 align-middle whitespace-normal break-words">{r.corelink}</td>
                    <td className="px-6 py-5 align-middle whitespace-normal break-words">{r.general}</td>
                    <td className="px-6 py-5 align-middle whitespace-normal break-words">{r.legacy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-gray-600 max-w-3xl mx-auto text-center mt-6">
            Where general AI lacks compliance and legacy systems lack agility, Highwatch delivers both, trusted intelligence that connects, understands, and acts.
          </p>
        </Layout>
      </section>

      {/* Desktop — Product Intelligence comparison + FAQ */}
      <div className="pi-intel-desktop">
        <section className="pi-intel-compare">
          <div className="pi-intel-compare__inner">
            <p className="pi-eyebrow">SEE THE DIFFERENCE</p>
            <h2>The Trusted Intelligence Layer Your Enterprise Stack Has Been Missing</h2>
            <p className="pi-intel-compare__lead">
              Highwatch goes beyond general AI and legacy enterprise systems — it unifies every
              system, understands your business context, and acts securely across workflows.
              Where others assist, Corelink executes, compliantly, contextually, and in seconds.
            </p>
            <div className="pi-intel-compare__table-wrap">
              <table className="pi-intel-compare__table">
                <thead>
                  <tr>
                    <th>Features</th>
                    <th>Highwatch AI</th>
                    <th>General AI Platforms</th>
                    <th>Legacy Enterprise Platforms</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.feature}>
                      <td>{r.feature}</td>
                      <td>{r.corelink}</td>
                      <td>{r.general}</td>
                      <td>{r.legacy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="pi-intel-compare__footnote">
              Where general AI lacks compliance and legacy systems lack agility, Highwatch delivers
              both — trusted intelligence that connects, understands, and acts.
            </p>
          </div>
        </section>
        <FAQSection variant="home" />
      </div>

      {/* Desktop — Product Insurance FAQ */}
      <div className="pi-desktop">
        <FAQSection variant="home" />
      </div>
    </>
  );
}
